import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import type {
  CreateAppointmentGroupingRequest,
  AppointmentGroupingResponse,
  UpdateAppointmentGroupingRequest
} from 'generated';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import { LoadingButton } from '@mui/lab';
import { Grid, TextField, DialogActions, Button } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import {
  ControlledPlanUnitSelector,
  ControlledTextField,
  Dialog
} from 'components/shared';

import addRouteSchema from './add-route-schema';

const schema = addRouteSchema();
type Form = InferType<typeof schema>;

const start = dayjs().set('hour', 7).set('minute', 0);

const defaultValues = {
  code: '',
  description: '',
  start,
  minuteDuration: 0,
  hourDuration: 0,
  organizationalUnit: null
};

function convertHourMinutesToDayjs(hourMinutes: string) {
  const [hour, minutes] = hourMinutes
    .split(':')
    .map((str) => parseInt(str, 10));

  return dayjs().hour(hour).minute(minutes);
}

type Props = {
  open: boolean;
  handleClose: () => void;
  appointmentGroup?: AppointmentGroupingResponse;
};

export default function AddRouteDialog({
  open,
  handleClose,
  appointmentGroup
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: createAppointmentGrouping, isLoading } =
    mutations.useCreateAppointmentGrouping();
  const { mutate: updateAppointmentGrouping } =
    mutations.useUpdateAppointmentGrouping();

  const { control, handleSubmit, setValue, reset, watch } = useForm<Form>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  function getOrganizationalUnit(
    appointmentGroup: AppointmentGroupingResponse
  ) {
    if (!appointmentGroup?.organizationalUnit) return undefined;
    return {
      id: appointmentGroup.organizationalUnit?.id,
      code: appointmentGroup.organizationalUnit?.code,
      name: appointmentGroup.organizationalUnit?.name
    };
  }

  const resetForm = () => {
    if (!appointmentGroup) {
      reset(defaultValues);
      return;
    }

    const ms = (appointmentGroup.durationInSeconds || 0) * 1000;

    reset({
      organizationalUnit: getOrganizationalUnit(appointmentGroup),
      code: appointmentGroup.code,
      description: appointmentGroup.description,
      start: convertHourMinutesToDayjs(appointmentGroup.start ?? '07:00'),
      minuteDuration: dayjs(ms).get('m'),
      hourDuration: dayjs(ms).get('h') - 1 // 0 - 23, so subtracting 1
    });
  };

  useEffect(() => resetForm(), [appointmentGroup]);

  const { start, minuteDuration, hourDuration } = watch();

  useEffect(() => {
    const endTime = start
      .add(hourDuration, 'hour')
      .add(minuteDuration, 'minute');
    setValue('endTime', endTime);
  }, [start, minuteDuration, hourDuration]);

  async function onSubmit(data) {
    const {
      code,
      description,
      organizationalUnit,
      start,
      minuteDuration,
      hourDuration
    } = data;
    const organizationalUnitId = organizationalUnit?.id;
    const durationInSeconds = hourDuration * 3600 + minuteDuration * 60;

    if (appointmentGroup) {
      const payload: UpdateAppointmentGroupingRequest = {
        entityId: appointmentGroup.entityId,
        organizationalUnitId,
        code,
        description,
        start: start.format('HH:mm'),
        durationInSeconds
      };

      await updateAppointmentGrouping(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getAppointmentGroupings.name);
          handleClose();
          resetForm();
        }
      });
    } else {
      const payload: CreateAppointmentGroupingRequest = {
        organizationalUnitId,
        code,
        description,
        start: start.format('HH:mm'),
        durationInSeconds
      };

      await createAppointmentGrouping(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getAppointmentGroupings.name);
          handleClose();
          resetForm();
        }
      });
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        title={
          appointmentGroup
            ? `${appointmentGroup.code} wijzigen`
            : 'Route toevoegen'
        }
        open={open}
        handleClose={() => {
          resetForm();
          handleClose();
        }}
      >
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ControlledPlanUnitSelector
                name="organizationalUnit"
                control={control}
                hideGroups
                hideNavigation
                useEmployeePlanunits
                isSingleSelect
                doNotPreselectOnlyOption
                doNotPersist
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="code"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    fullWidth
                    {...field}
                    label="Route nummer"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message ?? null}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={8}>
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    fullWidth
                    {...field}
                    label="Route naam"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message ?? null}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="start"
                control={control}
                render={({ field: { ref, ...fieldProps }, fieldState }) => {
                  return (
                    <TimePicker
                      {...fieldProps}
                      ampm={false}
                      inputRef={ref}
                      label={t('startTime')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!fieldState?.error?.type}
                          helperText={fieldState?.error?.message}
                        />
                      )}
                    />
                  );
                }}
              />
            </Grid>
            <Grid item>
              <ControlledTextField
                variant="standard"
                name="hourDuration"
                label={t('hours')}
                control={control}
                sx={{ width: '60px', mt: 0.4 }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
                onBlur={() => {
                  if (!minuteDuration && hourDuration)
                    setValue('minuteDuration', 0);
                  if (!hourDuration) setValue('hourDuration', 0);
                }}
              />
            </Grid>

            <Grid item>
              <ControlledTextField
                variant="standard"
                name="minuteDuration"
                label={t('minutes')}
                control={control}
                sx={{ width: '60px', mt: 0.4 }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
                onBlur={() => {
                  if (minuteDuration && !hourDuration)
                    setValue('hourDuration', 0);
                  if (!minuteDuration) setValue('minuteDuration', 0);
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    {...field}
                    ampm={false}
                    label={t('endTime')}
                    value={field.value}
                    readOnly
                    disabled
                    renderInput={(params) => <TextField {...params} />}
                  />
                )}
              />
            </Grid>
          </Grid>
          <DialogActions sx={{ mt: 3 }}>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              onClick={() => {
                handleClose();
                resetForm();
              }}
            >
              {t('cancel')}
            </Button>
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              color="primary"
            >
              {t('save')}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
}
